<template>
    <section
        class="border-y border-slate-200 bg-slate-50"
        aria-labelledby="feature-pages-heading"
    >
        <div class="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8">
            <div class="max-w-3xl">
                <h2
                    id="feature-pages-heading"
                    class="text-3xl font-bold tracking-tight text-slate-900"
                >
                    Explore features built for coaches, consultants, and solo
                    experts
                </h2>
                <p class="mt-3 text-slate-600">
                    Dive deeper into the capabilities that help expert-led
                    businesses run client-facing sessions with less friction.
                </p>
            </div>

            <ul class="mt-10 grid gap-4 md:grid-cols-2">
                <li
                    v-for="featurePageLink in featurePageLinks"
                    :key="featurePageLink.title"
                    class="contents"
                >
                    <article
                        class="rounded-xl border border-slate-200 bg-white p-6"
                        :aria-labelledby="featurePageLink.headingId"
                    >
                        <p
                            v-if="featurePageLink.badgeText !== null"
                            class="mb-2 inline-flex rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-amber-900"
                        >
                            {{ featurePageLink.badgeText }}
                        </p>

                        <h3
                            :id="featurePageLink.headingId"
                            class="text-xl font-bold text-slate-900"
                        >
                            {{ featurePageLink.title }}
                        </h3>
                        <p class="mt-2 text-sm text-slate-600">
                            {{ featurePageLink.summary }}
                        </p>

                        <p class="mt-4">
                            <a
                                :href="featurePageLink.url"
                                class="inline-flex items-center gap-2 text-sm font-bold text-primary hover:text-primary-shade"
                                :aria-label="`${featurePageLink.ctaLabel}: ${featurePageLink.title}`"
                            >
                                {{ featurePageLink.ctaLabel }}
                                <LandingPageIcon
                                    name="arrow-right"
                                    class="h-4 w-4"
                                    aria-hidden="true"
                                />
                            </a>
                        </p>
                    </article>
                </li>
            </ul>
        </div>
    </section>
</template>

<script setup lang="ts">
    import LandingPageIcon from '@/Marketing/LandingPage/Component/LandingPageIcon.vue'
    import { RoutePathMarketingEnum } from '@/Shared/AutoGenerated/RoutePathEnum'
    import { UrlFactory } from '@/Shared/Url/UrlFactory'

    interface FeaturePageLink {
        title: string
        summary: string
        url: string
        ctaLabel: string
        badgeText: string | null
        headingId: string
    }

    const urlFactory = new UrlFactory()

    const featurePageLinks: FeaturePageLink[] = [
        {
            title: 'Group Scheduling, without the chaos',
            summary:
                'Run group services with attendee caps, per-attendee payments, intake fields, and spot-level cancellations.',
            url: urlFactory.makeMarketingRoutePageUrl(
                RoutePathMarketingEnum.Feature_GroupMeetings,
            ),
            ctaLabel: 'Explore group meetings',
            badgeText: null,
            headingId: 'feature-page-link-group-meetings',
        },
        {
            title: 'Recurring Meetings, on autopilot',
            summary:
                'Set a recurring cadence once, keep clients on consistent time slots, and simplify ongoing sessions.',
            url: urlFactory.makeMarketingRoutePageUrl(
                RoutePathMarketingEnum.Feature_RecurringMeetings,
            ),
            ctaLabel: 'Explore recurring meetings',
            badgeText: null,
            headingId: 'feature-page-link-recurring-meetings',
        },
        {
            title: 'SMS and WhatsApp reminders',
            summary:
                'Reach clients in high-visibility channels with configurable reminder timing and message controls.',
            url: urlFactory.makeMarketingRoutePageUrl(
                RoutePathMarketingEnum.Feature_SmsWhatsAppReminders,
            ),
            ctaLabel: 'Explore SMS/WhatsApp reminders',
            badgeText: 'Upcoming',
            headingId: 'feature-page-link-sms-whatsapp-reminders',
        },
        {
            title: 'Automations',
            summary:
                'Create trigger-based workflows with flexible actions and execution logs as your process gets more sophisticated.',
            url: urlFactory.makeMarketingRoutePageUrl(
                RoutePathMarketingEnum.Feature_Automations,
            ),
            ctaLabel: 'Explore automations',
            badgeText: 'Upcoming',
            headingId: 'feature-page-link-automations',
        },
    ]
</script>
